import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Mail, Loader2, Users, AlertTriangle, Clock, CheckCircle, Send, RefreshCw,
  Calendar, UserCheck, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RenewalRemindersPage = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState({ expiring_soon: [], expired: [], total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('expiring');
  const [results, setResults] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/renewal-candidates`, { headers });
      setData(res.data);
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };

  useEffect(() => { fetchCandidates(); }, []);

  const list = tab === 'expiring' ? data.expiring_soon : data.expired;
  const filtered = list.filter(u =>
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(u => u.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handleSend = async () => {
    if (selected.size === 0) return toast.error('Sélectionnez au moins un membre');
    setSending(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/api/admin/send-renewal-reminders`, {
        user_ids: Array.from(selected)
      }, { headers });
      setResults(res.data);
      if (res.data.sent > 0) toast.success(`${res.data.sent} rappel(s) envoyé(s)`);
      if (res.data.failed > 0) toast.error(`${res.data.failed} echec(s)`);
      setSelected(new Set());
      fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSending(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const isExpired = (d) => {
    if (!d) return false;
    try { return new Date(d) < new Date(); }
    catch { return false; }
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-6" data-testid="renewal-reminders-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
              <Mail className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Rappels d'adhesion</h1>
              <p className="text-xs text-gray-500">Envoyez des rappels de renouvellement aux membres</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchCandidates} className="gap-1.5" data-testid="refresh-btn">
              <RefreshCw size={14} /> Rafraichir
            </Button>
            <Button onClick={handleSend} disabled={sending || selected.size === 0}
              className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white gap-1.5" data-testid="send-reminders-btn">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Envoyer ({selected.size})
            </Button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock size={18} className="text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data.expiring_soon.length}</p>
                <p className="text-[11px] text-gray-500">Expirent bientot</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle size={18} className="text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data.expired.length}</p>
                <p className="text-[11px] text-gray-500">Adhesions expirees</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Users size={18} className="text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{data.total}</p>
                <p className="text-[11px] text-gray-500">Total a relancer</p>
              </div>
            </div>
          </div>
        </div>

        {/* Results feedback */}
        {results && (
          <div className={`rounded-xl border p-4 ${results.sent > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="send-results">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} className={results.sent > 0 ? 'text-emerald-600' : 'text-amber-600'} />
              <span className="text-sm font-bold">{results.sent} envoyé(s), {results.failed} echec(s)</span>
            </div>
            {results.errors.length > 0 && (
              <div className="mt-2 space-y-1">
                {results.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tabs + Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            <button onClick={() => { setTab('expiring'); setSelected(new Set()); }}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'expiring' ? 'bg-white shadow text-amber-700' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="tab-expiring">
              Expirent bientot ({data.expiring_soon.length})
            </button>
            <button onClick={() => { setTab('expired'); setSelected(new Set()); }}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'expired' ? 'bg-white shadow text-red-700' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="tab-expired">
              Expirees ({data.expired.length})
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
              className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 text-sm w-64" data-testid="search-members" />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[#35AFE4]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-12 text-center">
            <UserCheck size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {tab === 'expiring' ? 'Aucun membre avec adhesion expirant bientot' : 'Aucune adhesion expiree'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm" data-testid="members-table">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-4 py-3 text-left w-10">
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll} className="rounded border-gray-300" data-testid="select-all" />
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Membre</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Échéance</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Dernier rappel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((u, i) => {
                  const expired = isExpired(u.subscription_end_date);
                  return (
                    <tr key={u.id} className={`hover:bg-gray-50/50 transition-colors ${selected.has(u.id) ? 'bg-blue-50/30' : ''}`}
                      data-testid={`member-row-${i}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                          className="rounded border-gray-300" data-testid={`select-member-${i}`} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-gray-800">Dr {u.first_name} {u.last_name}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          {u.role === 'ophthalmologist' ? 'Praticien' : 'Interne'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${expired ? 'text-red-600' : 'text-amber-600'}`}>
                          <Calendar size={11} /> {formatDate(u.subscription_end_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {u.last_renewal_reminder ? formatDate(u.last_renewal_reminder) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RenewalRemindersPage;
